import { captureException } from '@sentry/react';
import { redirect } from 'react-router';

import { safeRedirectPath } from '~/auth/redirectPath';
import {
  deleteFirebaseAuthStorage,
  endImpersonation,
  endSession,
  getAccessToken as getSessionAccessToken,
  onSessionChangedElsewhere,
  onSessionEnded,
  readImpersonation,
  readSession,
  refreshAccessToken,
  revokePendingSessions,
  startSession,
} from '~/auth/tokenStore';
import { loadDocument, reloadDocument, withLoginRedirectSearchParam } from '~/auth/utils';
import {
  authLoginCreate,
  authMagicLinkConsumeCreate,
  authMagicLinkCreate,
  authPasswordResetConfirmCreate,
  authPasswordResetCreate,
  authPasswordSetCreate,
} from '~/data/generated/endpoints/auth/auth';
import { impersonateTokenCreate } from '~/data/generated/endpoints/impersonate/impersonate';
import type { AuthTokenPair, ImpersonationToken } from '~/data/generated/model';
import { configureLiftApi } from '~/data/liftApiMutator';
import { type UserRole } from '~/data/schemas/user';

const { VITE_API_HOST: API_HOST } = import.meta.env;

// A sign-in link is two-legged: the post-login destination is known when the
// link is *requested* and needed when the link is *opened*. The emailed link
// is built by the API and carries only the token, so the destination is
// stashed here and read back when the link is opened in the same browser. A
// link opened elsewhere lands on the role's default page.
const POST_SIGN_IN_REDIRECT_KEY = 'postSignInRedirect';

// The auth endpoints run no authenticator. An empty Authorization header also
// keeps the mutator from looking up (and possibly refreshing) a stored token
// for a request that must not depend on one.
const ANONYMOUS: RequestInit = { headers: { Authorization: '' } };

// Exposed so callers outside the generated SDK (e.g. the Vercel AI SDK's
// useChat transport on the Intelligence page) can forward the same Bearer
// token to streaming endpoints. A stream cannot be retried after a 401, which
// is why this refreshes a token shortly before it expires rather than after.
// Don't bypass this for custom token logic — the Django side trusts whatever
// this returns.
export const getAccessToken = getSessionAccessToken;

// Same source of truth as the SDK mutator's apiHost. The AI SDK's
// DefaultChatTransport does its own fetch + SSE parsing and doesn't
// route through the generated SDK, so it needs the base URL directly.
export const apiHost = API_HOST ?? '';

/** Set while a super-admin is signed in as another user via the impersonation tool. */
export type ImpersonationState = {
  impersonatedEmail?: string;
};

const authHelper = () => ({
  /**
   * Revoke the session server-side and clear this browser's copy. The local
   * session is cleared even if the revoke request fails; that failure is
   * reported to Sentry rather than keeping the user signed in.
   */
  async signOut() {
    const { revoked } = await endSession();
    if (!revoked) {
      captureException(new Error('Sign-out could not revoke the session server-side'));
    }
  },

  /**
   * Ask the API to email a password reset link. It answers the same way
   * whether or not the address has an account.
   */
  async requestPasswordReset(email: string) {
    await authPasswordResetCreate({ email }, ANONYMOUS);
  },

  /**
   * Set a new password with the token from an emailed reset link. Resolves to
   * the token pair of the session the reset starts, which the caller passes to
   * `startSession`; every earlier session of the account has already ended.
   */
  async confirmPasswordReset(token: string, password: string) {
    const { data } = await authPasswordResetConfirmCreate({ token, password }, ANONYMOUS);
    return data as AuthTokenPair;
  },

  /**
   * Give the signed-in account a password when it has none (for example one
   * that has only ever used sign-in links).
   */
  async setPassword(password: string) {
    await authPasswordSetCreate({ password });
  },

  async loginUserWithEmailAndPassword(email: string, password: string) {
    const { data } = await authLoginCreate({ email, password }, ANONYMOUS);
    return startSession(data as AuthTokenPair);
  },

  /**
   * Email a passwordless sign-in link ("magic link") to `email`.
   *
   * The API decides whether the address gets an email and answers 202 either
   * way, so the response never reveals which emails have accounts; callers
   * show a generic "check your email" message regardless. It also never creates
   * an account. Any other failure (bad input, throttling, a 5xx) is thrown, so
   * the caller surfaces a real error rather than claiming a link was sent.
   *
   * `redirectTo` is where to go after the link signs the user in. The API puts
   * it in the link, so it holds on any device; it is also kept here, for a link
   * that does not carry it.
   */
  async sendSignInLink(email: string, redirectTo: string | null) {
    await authMagicLinkCreate({ email, redirectTo: safeRedirectPath(redirectTo) }, ANONYMOUS);
    if (redirectTo) {
      window.localStorage.setItem(POST_SIGN_IN_REDIRECT_KEY, redirectTo);
    } else {
      window.localStorage.removeItem(POST_SIGN_IN_REDIRECT_KEY);
    }
  },

  /**
   * Complete a magic-link sign-in with the token from the emailed link. The
   * token alone identifies the account, and works once. Bootstraps the local
   * session exactly like the password login path. Returns the signed-in user
   * and the destination stored when the link was requested, if any.
   */
  async completeSignInWithMagicLink(token: string) {
    const { data } = await authMagicLinkConsumeCreate({ token }, ANONYMOUS);
    // Read before signing in: unless this account was already signed in here,
    // signing in clears localStorage, stored destination included.
    const redirectTo = window.localStorage.getItem(POST_SIGN_IN_REDIRECT_KEY);
    const user = await startSession(data as AuthTokenPair);
    window.localStorage.removeItem(POST_SIGN_IN_REDIRECT_KEY);
    return { user, redirectTo };
  },

  /**
   * Sign in as the user with `email` (super-admins only). The admin's own
   * session is set aside rather than ended, so `exitImpersonation` can resume
   * it. Local data is cleared first so none of it leaks into the target's
   * session, and everything done afterwards is done as the target.
   */
  async impersonate(email: string) {
    // The mutator throws on non-2xx, so on the success path `data` is always
    // the token body (not the 400/404/409 error member of the response union).
    const { access, refresh } = (await impersonateTokenCreate({ email }))
      .data as ImpersonationToken;
    return startSession({ access, refresh }, { impersonating: true });
  },

  /**
   * End the impersonation server-side and sign the admin back in as
   * themselves. Resolves to the resumed user, or null when the admin's session
   * could not be resumed and the browser was signed out instead. A failed
   * revoke is reported to Sentry; the target's tokens are gone from this
   * browser either way.
   */
  async exitImpersonation() {
    const { user, revoked } = await endImpersonation();
    if (!revoked) {
      captureException(new Error('Exiting impersonation could not revoke the session server-side'));
    }
    return user;
  },

  /**
   * Returns the active impersonation state, or null when the current session
   * is the user's own (read by the impersonation banner).
   */
  getImpersonation(): ImpersonationState | null {
    const impersonation = readImpersonation();
    return impersonation ? { impersonatedEmail: impersonation.target.email || undefined } : null;
  },

  /**
   * `throw`s a `Response` if the user is not logged in. Otherwise, returns the
   * user.
   *
   * Reads only the stored session, so it makes no request. A session the API
   * has since revoked is noticed on the next API call, whose refresh fails and
   * sends the browser back to `/login`.
   */
  async requireUser(
    redirectTo?: string,
    httpCode = 302,
  ): Promise<{
    userRole: UserRole;
    /** The Django user id: the Mixpanel, LaunchDarkly and Sentry identity. */
    userId: string;
    email: string | undefined;
    isLiftaiAdmin: boolean;
  }> {
    const session = readSession();

    if (!session) {
      throw redirect(`/login?${withLoginRedirectSearchParam(redirectTo)}`, httpCode);
    }

    const { id, email, role: userRole, isLiftaiAdmin } = session.user;

    // eAMP is unusable without a user role.
    if (!userRole) {
      throw new Error('User is not provisioned for eAMP');
    }

    return {
      userRole,
      userId: id,
      email: email || undefined,
      isLiftaiAdmin,
    };
  },
});

export const liftAiAuth = () => {
  return authHelper();
};

const liftApiAuthToken = import.meta.env.STORYBOOK ? () => Promise.resolve('') : getAccessToken;

// Configure the orval-generated SDK (AMP-312). Generated operations call the
// shared mutator, which reads this config; set it once at module load so any
// hook/component using the generated client works without per-call wiring.
if (API_HOST) {
  configureLiftApi({
    authToken: liftApiAuthToken,
    refreshAuthToken: import.meta.env.STORYBOOK ? undefined : refreshAccessToken,
    apiHost: API_HOST,
    fetch,
  });
  if (!import.meta.env.STORYBOOK) {
    // Sessions a previous page gave up on but could not confirm were revoked.
    // Nothing awaits this, so a storage error (a full or blocked localStorage)
    // is reported here instead of surfacing as an unhandled rejection.
    revokePendingSessions().catch((error: unknown) => captureException(error));
  }
}

// The Firebase-era webapp kept its sign-in in IndexedDB. Removed once per load,
// so a browser that never signs out or in again still loses it.
if (!import.meta.env.STORYBOOK) {
  deleteFirebaseAuthStorage();
}

// A refused refresh means the session is over (signed out elsewhere, password
// reset, expired). Send the user to sign in again, back to where they were.
onSessionEnded(() => {
  const { pathname, search } = window.location;
  window.location.assign(`/login?${withLoginRedirectSearchParam(`${pathname}${search}`)}`);
});

// Another tab signed in as someone else, started or ended an impersonation, or
// signed out. What this document shows, and the identity it reports to
// analytics, Sentry and LaunchDarkly, belong to the account it loaded as, so
// load it again for the account now signed in. `getAccessToken` refuses to
// send the new account's token in the meantime.
onSessionChangedElsewhere((user) => {
  if (user) {
    reloadDocument();
    return;
  }
  const { pathname, search } = window.location;
  loadDocument(`/login?${withLoginRedirectSearchParam(`${pathname}${search}`)}`);
});
